import React from 'react';
import { useQuery } from '@tanstack/react-query';
import DashboardSection from './components/DashboardSection';
import DashboardPage from './components/DashboardPage';
import NationalQuotaSummary from './components/NationalQuotaSummary';
import useDashboardScope from './useDashboardScope';

import { statValue, readQuotaStats } from './stats';

const BankDashboard = () => {
  const { api, campaignYear, enabled, queryOptions, scope } = useDashboardScope();
  const invoiceQuery = useQuery({
    ...queryOptions,
    queryKey: ['invoice/stats/deposit_count/', ...scope],
    queryFn: api.list('invoice/stats/deposit_count/', { year: campaignYear }),
  });
  const quotaQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/stats/category/', ...scope],
    queryFn: api.list('trip/stats/category/', { year: campaignYear }),
  });
  const stats = invoiceQuery.data?.data?.data;
  const invoices = {
    pending: statValue(stats, 'Factures en attente'),
    pending_assigned: statValue(stats, 'Factures en attente assignées'),
    pending_shared: statValue(stats, 'Factures en attente non assignées'),
    paid: statValue(stats, 'Factures payées'),
    paid_amount: statValue(stats, 'Montant des factures payées'),
    pending_amount: statValue(stats, 'Montant des factures en attente'),
  };
  const quota = readQuotaStats(quotaQuery.data?.data?.data);
  const invoiceAvailable =
    invoices &&
    ['pending', 'pending_assigned', 'pending_shared', 'paid', 'paid_amount', 'pending_amount'].every((key) =>
      Number.isFinite(invoices[key])
    );
  const queries = [invoiceQuery, quotaQuery];
  return (
    <DashboardPage
      ariaLabel="Tableau de bord bancaire"
      title="Tableau de bord"
      subtitle={`Campagne ${campaignYear || 'indisponible'}`}
      enabled={enabled}
      queries={queries}
      hasCompleteData={!!(invoiceAvailable && quota)}
    >
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="grid min-w-0 gap-4">
          <DashboardSection title="File de traitement" query={invoiceQuery} available={invoiceAvailable}>
            {invoiceAvailable && (
              <>
                <p className="mt-1">
                  <strong className="block text-[44px] font-semibold leading-tight tracking-[-1.5px] text-emerald-800">
                    {invoices.pending.toLocaleString('fr-FR')}
                  </strong>
                  <span className="mt-1.5 block text-sm text-slate-500">Factures en attente</span>
                </p>
                <dl className="mt-5 grid grid-cols-2 gap-5 border-t pt-4">
                  <div>
                    <dd className="text-2xl font-semibold">{invoices.pending_assigned.toLocaleString('fr-FR')}</dd>
                    <dt className="mt-1 text-[13px] text-slate-500">assignées</dt>
                  </div>
                  <div className="border-l pl-5">
                    <dd className="text-2xl font-semibold">{invoices.pending_shared.toLocaleString('fr-FR')}</dd>
                    <dt className="mt-1 text-[13px] text-slate-500">file partagée non assignée</dt>
                  </div>
                </dl>
              </>
            )}
          </DashboardSection>
          <DashboardSection title="Factures payées" query={invoiceQuery} available={invoiceAvailable}>
            {invoiceAvailable && (
              <p className="mt-1">
                <strong className="block text-[44px] font-semibold leading-tight tracking-[-1.5px] text-emerald-800">
                  {invoices.paid.toLocaleString('fr-FR')}
                </strong>
                <span className="mt-1 block text-sm text-slate-500">factures</span>
              </p>
            )}
          </DashboardSection>
          <DashboardSection title="Situation financière" query={invoiceQuery} available={invoiceAvailable}>
            {invoiceAvailable && (
              <div className="mt-1">
                <p className="text-[30px] font-semibold leading-tight tracking-[-0.9px] text-emerald-800">
                  {invoices.paid_amount.toLocaleString('fr-FR')}{' '}
                  <span className="text-xs font-normal tracking-normal">F CFA</span>
                </p>
                <p className="mt-1 text-[13px] text-slate-500">Montant payé</p>
                <div className="mt-4 border-t pt-3">
                  <p className="text-[13px] text-slate-500">Montant en attente</p>
                  <p className="mt-1 text-base font-semibold">
                    {invoices.pending_amount.toLocaleString('fr-FR')} F CFA
                  </p>
                </div>
              </div>
            )}
          </DashboardSection>
        </div>
        <DashboardSection
          title="Quota national"
          help="Quota commun à toutes les sociétés. Seuls les pèlerins soldés occupent une place."
          query={quotaQuery}
          available={!!quota}
        >
          {quota && <NationalQuotaSummary quota={quota} />}
        </DashboardSection>
      </div>
    </DashboardPage>
  );
};
export default BankDashboard;
